import IconClose from "@shared/components/icons/Close"
import { useBreakpoint } from "@shared/components/untitledui/hooks/use-breakpoint"
import { ModalBody, ModalContent, ModalTitle } from "@shared/components/untitledui/modal"
import { cx } from "@shared/components/untitledui/util/cx"
import { changedSinceCopied } from "@shared/lib/followed-course"
import type { ParentCardStack, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked, useReactive } from "@supergrain/kernel/react"
import openModalAction from "cc-frontend/lib/actions/app-state/OPEN_MODAL"
import { followContext } from "cc-frontend/lib/actions/course/COURSE_FOLLOW_COURSE"
import type { MotionValue } from "motion/react"
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "motion/react"
import type { ReactNode } from "react"
import { useEffect, useLayoutEffect, useRef } from "react"
import { useDispatch } from "react-app/hooks/useDispatch"
import { Button as AriaButton, DialogTrigger } from "react-aria-components"
import {
  copiedLessonDay,
  followedCourseDays,
  formatShortDate,
  meetingDayAtOffset,
} from "./followed-course-modal/followed-course-dates"
import { FollowedCourseArrowIcon } from "./followed-course-modal/FollowedCourseArrowIcon"
import { FollowedCourseDatePicker } from "./followed-course-modal/FollowedCourseDatePicker"
import { FollowedCourseLessonColumn } from "./followed-course-modal/FollowedCourseLessonColumn"
import styles from "./FollowedCourseMenu.module.css"
import { useFollowedLesson } from "./use-followed-lesson"

interface Props {
  /** The class doing the following. */
  followingCourseId: string
  followedCourse: Course
  planbook: Planbook | null
  rotationCalendar: RotationCalendar | null
  /** The host lesson's dateString, used as the initial date for the followed lesson carousel. */
  dateString: string
  /** The lesson the host lesson was copied from, which may be one of the followed class's. */
  parentCardStack: ParentCardStack | null
  /** Just the followed class's name, for while the planner shows lessons collapsed. */
  isCompact: boolean
  /** If undefined, the option to copy a lesson is skipped. */
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
}

export default tracked(function FollowedCourseMenu({
  followingCourseId,
  followedCourse,
  planbook,
  rotationCalendar,
  dateString,
  parentCardStack,
  isCompact,
  onCopyFollowedLesson,
}: Props) {
  const followedCourseTitle = followedCourse.attributes.title
  if (planbook === null || rotationCalendar === null) {
    return (
      <Trigger
        followedCourseTitle={followedCourseTitle}
        lessonTitle={null}
        dateString={dateString}
        isDisabled
      />
    )
  }

  return (
    <LoadedFollowedCourseMenu
      followingCourseId={followingCourseId}
      followedCourse={followedCourse}
      followedCourseTitle={followedCourseTitle}
      planbook={planbook}
      rotationCalendar={rotationCalendar}
      dateString={dateString}
      parentCardStack={parentCardStack}
      isCompact={isCompact}
      onCopyFollowedLesson={onCopyFollowedLesson}
    />
  )
})

const LoadedFollowedCourseMenu = tracked(function LoadedFollowedCourseMenu({
  followingCourseId,
  followedCourse,
  followedCourseTitle,
  planbook,
  rotationCalendar,
  dateString,
  parentCardStack,
  isCompact,
  onCopyFollowedLesson,
}: {
  followingCourseId: string
  followedCourse: Course
  followedCourseTitle: string
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  parentCardStack: ParentCardStack | null
  isCompact: boolean
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
}) {
  const dispatch = useDispatch()
  const { lesson } = useFollowedLesson(followedCourse, planbook, rotationCalendar, dateString)
  // The copied lesson may since have been erased; then there's nothing to flag.
  const copiedDay =
    parentCardStack === null ? null : copiedLessonDay(followedCourse, parentCardStack)
  const { lesson: copiedLesson } = useFollowedLesson(
    followedCourse,
    planbook,
    rotationCalendar,
    copiedDay,
  )

  // It isn't opened through the modal manager, which records every other modal's opening.
  const recordOpen = (isOpen: boolean) => {
    if (!isOpen) return
    dispatch(
      openModalAction({
        modalName: "followed-course-lessons",
        context: {
          ...followContext({ followingCourseId, followedCourseId: followedCourse.id }),
          dateString,
        },
      }),
    )
  }

  return (
    <DialogTrigger onOpenChange={recordOpen}>
      <Trigger
        followedCourseTitle={followedCourseTitle}
        lessonTitle={
          isCompact || lesson.status !== "loaded"
            ? null
            : lesson.lesson.attributes.title || "Untitled lesson"
        }
        dateString={dateString}
        isCopiedLessonChanged={
          !isCompact &&
          parentCardStack !== null &&
          copiedLesson.status === "loaded" &&
          changedSinceCopied(parentCardStack, copiedLesson.lesson)
        }
      />
      <ModalContent
        size="fullscreen"
        closeButton={false}
        overlay={{
          className:
            "grid-rows-[4rem_auto_1fr] bg-black/20 backdrop-blur-none sm:grid-rows-[4rem_auto_1fr]",
        }}
        className="h-[calc(100dvh-1rem)] w-full overflow-visible sm:h-[min(56rem,calc(100dvh-6rem))] sm:w-[min(76rem,calc(100vw-8rem))] [&>[data-slot=dialog]]:h-full [&>[data-slot=dialog]]:overflow-visible"
      >
        {({ close }) => (
          <FollowedCourseModal
            followedCourse={followedCourse}
            planbook={planbook}
            rotationCalendar={rotationCalendar}
            dateString={dateString}
            parentCardStack={parentCardStack}
            onCopyFollowedLesson={
              onCopyFollowedLesson === undefined
                ? undefined
                : (followedLesson, followedDateString) => {
                    onCopyFollowedLesson(followedLesson, followedDateString)
                    close()
                  }
            }
          />
        )}
      </ModalContent>
    </DialogTrigger>
  )
})

function Trigger({
  followedCourseTitle,
  lessonTitle,
  dateString,
  isCopiedLessonChanged = false,
  isDisabled = false,
}: {
  followedCourseTitle: string
  lessonTitle: string | null
  dateString: string
  isCopiedLessonChanged?: boolean
  isDisabled?: boolean
}) {
  return (
    <AriaButton
      data-test="followed course menu trigger"
      isDisabled={isDisabled}
      className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 rounded py-1.5 text-left [overflow-wrap:anywhere] outline-hidden hover:underline focus-visible:outline-2 focus-visible:outline-current disabled:cursor-default disabled:hover:no-underline"
    >
      <span className="font-bold" data-test="followed course name">
        Following: {followedCourseTitle}
      </span>
      {lessonTitle !== null && (
        <span data-test="followed lesson name">
          {formatShortDate(dateString)}: {lessonTitle}
        </span>
      )}
      {isCopiedLessonChanged && (
        <span className="italic" data-test="copied lesson changed">
          Changed since you copied it
        </span>
      )}
    </AriaButton>
  )
}

const FollowedCourseModal = tracked(function FollowedCourseModal({
  followedCourse,
  planbook,
  rotationCalendar,
  dateString,
  parentCardStack,
  onCopyFollowedLesson,
}: {
  followedCourse: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  parentCardStack: ParentCardStack | null
  onCopyFollowedLesson?: (followedLesson: UnifiedCardStack, followedDateString: string) => void
}) {
  const isWide = useBreakpoint("sm")
  const visibleCount = isWide ? 3 : 1
  const visibleRadius = (visibleCount - 1) / 2
  const carousel = useReactive({
    anchorDate: dateString,
    centerIndex: 0,
    transitionDirection: 0 as 1 | 0 | -1,
  })
  const scrollIndex = useMotionValue(0)
  const columnWidth = useMotionValue(0)
  const scrollAnimation = useRef<{ stop: () => void } | null>(null)
  const lessonWindowRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  const days = followedCourseDays(followedCourse, planbook, rotationCalendar)
  const dayAt = (index: number) => meetingDayAtOffset(carousel.anchorDate, index, days)
  const centerDay = dayAt(carousel.centerIndex) ?? carousel.anchorDate
  const canStep = (direction: 1 | -1) => dayAt(carousel.centerIndex + direction) !== null
  useEffect(() => () => scrollAnimation.current?.stop(), [])
  useLayoutEffect(() => {
    const lessonWindow = lessonWindowRef.current
    if (lessonWindow === null) return

    const updateColumnWidth = () => columnWidth.set(lessonWindow.clientWidth / visibleCount)
    updateColumnWidth()
    const animationFrame = window.requestAnimationFrame(updateColumnWidth)
    const observer = new ResizeObserver(updateColumnWidth)
    observer.observe(lessonWindow)
    return () => {
      window.cancelAnimationFrame(animationFrame)
      observer.disconnect()
    }
  }, [columnWidth, visibleCount])
  useEffect(() => {
    scrollAnimation.current?.stop()
    scrollIndex.set(carousel.centerIndex)
    carousel.transitionDirection = 0
  }, [carousel, scrollIndex, visibleRadius])
  const step = (nextDirection: 1 | -1) => {
    if (!canStep(nextDirection)) return
    if (lessonWindowRef.current !== null) {
      columnWidth.set(lessonWindowRef.current.clientWidth / visibleCount)
    }
    const nextCenterIndex = carousel.centerIndex + nextDirection
    scrollAnimation.current?.stop()
    const adjacentIndex = nextCenterIndex - nextDirection
    if (
      (nextDirection === 1 && scrollIndex.get() < adjacentIndex) ||
      (nextDirection === -1 && scrollIndex.get() > adjacentIndex)
    ) {
      scrollIndex.set(adjacentIndex)
    }
    carousel.transitionDirection = nextDirection
    carousel.centerIndex = nextCenterIndex
    if (reduceMotion) {
      scrollIndex.set(nextCenterIndex)
      carousel.transitionDirection = 0
      return
    }
    scrollAnimation.current = animate(scrollIndex, nextCenterIndex, {
      type: "spring",
      stiffness: 220,
      damping: 34,
      mass: 1,
      velocity: 0,
      restDelta: 0.00025,
      restSpeed: 0.0025,
      onComplete: () => {
        if (carousel.centerIndex !== nextCenterIndex) return
        carousel.transitionDirection = 0
      },
    })
  }
  const selectDate = (nextDate: string) => {
    scrollAnimation.current?.stop()
    scrollIndex.set(0)
    carousel.anchorDate = nextDate
    carousel.centerIndex = 0
    carousel.transitionDirection = 0
  }
  const { centerIndex, transitionDirection } = carousel
  const firstLessonIndex = centerIndex - visibleRadius - (transitionDirection === 1 ? 1 : 0)
  const lastLessonIndex = centerIndex + visibleRadius + (transitionDirection === -1 ? 1 : 0)
  // Past either end of the school year there's no day, so no column.
  const lessonDays = Array.from(
    { length: lastLessonIndex - firstLessonIndex + 1 },
    (_, offset) => ({
      lessonIndex: firstLessonIndex + offset,
      day: dayAt(firstLessonIndex + offset),
    }),
  ).filter((lessonDay): lessonDay is { lessonIndex: number; day: string } => lessonDay.day !== null)
  const visibleRange = isWide
    ? { first: dayAt(centerIndex - 1) ?? centerDay, last: dayAt(centerIndex + 1) ?? centerDay }
    : null
  // A copy from a different class predates switching classes, so it says nothing about this one.
  const copiedLessonRemoved =
    parentCardStack !== null &&
    parentCardStack.courseId === followedCourse.id &&
    copiedLessonDay(followedCourse, parentCardStack) === null

  return (
    <>
      <AriaButton
        slot="close"
        aria-label="Close followed class"
        data-test="followed course modal close"
        className="absolute top-2 right-2 z-50 flex size-12 cursor-pointer items-center justify-center rounded-full bg-white text-hint shadow-md outline-hidden hover:text-link-blue focus-visible:outline-2 focus-visible:outline-focus-ring sm:-top-15 sm:right-0"
      >
        <IconClose className="size-5" />
      </AriaButton>
      <ModalBody
        className="h-full scrollbar-gutter-stable overflow-x-auto overflow-y-scroll overscroll-contain rounded-2xl bg-gray-100 p-0"
        data-test="followed course modal body"
      >
        <div
          className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start overflow-x-clip px-3 pt-16 pb-6 sm:min-w-[60rem] sm:px-6 sm:pt-7"
          data-test="followed course modal grid"
        >
          <div className="col-start-2 row-start-1 px-1.5">
            <ModalTitle
              level={2}
              className={`${styles.planbookCourse} m-0`}
              data-test="followed course menu heading"
            >
              {planbook.attributes.title}: {followedCourse.attributes.title}
            </ModalTitle>
            {copiedLessonRemoved && (
              <p
                className="mt-1 text-center text-sm text-gray-500"
                data-test="followed course menu copied status"
              >
                The lesson you copied for this day is no longer in this class.
              </p>
            )}
          </div>
          <div
            className="relative col-start-2 row-start-2 self-center"
            data-test="followed course date navigation"
          >
            <div className="absolute top-1/2 left-2.25 z-20 -translate-y-1/2">
              <StepButton
                label="Previous lessons"
                direction={-1}
                step={step}
                isDisabled={!canStep(-1)}
              />
            </div>
            <FollowedCourseDatePicker
              value={centerDay}
              visibleRange={visibleRange}
              days={days}
              onChange={selectDate}
            />
            <div className="absolute top-1/2 right-2.25 z-20 -translate-y-1/2">
              <StepButton label="Next lessons" direction={1} step={step} isDisabled={!canStep(1)} />
            </div>
          </div>
          <div className="relative z-10 col-start-1 row-start-3 mt-5">
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-[calc(100%+1.5rem)] bg-linear-to-r from-gray-100 to-transparent"
              data-test="followed course previous edge fade"
            />
          </div>
          <div
            ref={lessonWindowRef}
            className="relative col-start-2 row-start-3 mt-5 grid w-full min-w-0 overflow-visible [contain:inline-size]"
            data-test="followed course lesson window"
          >
            {lessonDays.map(({ lessonIndex, day }) => {
              const slot = lessonIndex - centerIndex + visibleRadius
              const isVisible = slot >= 0 && slot < visibleCount
              return (
                <SlidingLessonColumn
                  key={day}
                  lessonIndex={lessonIndex}
                  scrollIndex={scrollIndex}
                  columnWidth={columnWidth}
                  centerSlot={visibleRadius}
                  visibleCount={visibleCount}
                  isVisible={isVisible}
                  day={day}
                >
                  <FollowedCourseLessonColumn
                    followedCourse={followedCourse}
                    planbook={planbook}
                    rotationCalendar={rotationCalendar}
                    day={day}
                    isCurrent={day === dateString}
                    parentCardStack={parentCardStack}
                    onCopyFollowedLesson={onCopyFollowedLesson}
                  />
                </SlidingLessonColumn>
              )
            })}
          </div>
          <div className="relative z-10 col-start-3 row-start-3 mt-5">
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-[calc(100%+1.5rem)] bg-linear-to-r from-transparent to-gray-100"
              data-test="followed course next edge fade"
            />
          </div>
        </div>
      </ModalBody>
    </>
  )
})

function SlidingLessonColumn({
  lessonIndex,
  scrollIndex,
  columnWidth,
  centerSlot,
  visibleCount,
  isVisible,
  day,
  children,
}: {
  lessonIndex: number
  scrollIndex: MotionValue<number>
  columnWidth: MotionValue<number>
  centerSlot: number
  visibleCount: number
  isVisible: boolean
  day: string
  children: ReactNode
}) {
  const x = useTransform(() => (lessonIndex - scrollIndex.get() + centerSlot) * columnWidth.get())
  const opacity = useTransform(() => {
    const slot = lessonIndex - scrollIndex.get() + centerSlot
    if (slot < 0) return Math.max(0, slot + 1)
    if (slot > visibleCount - 1) return Math.max(0, visibleCount - slot)
    return 1
  })

  return (
    <motion.div
      style={{ x, opacity, width: columnWidth }}
      aria-hidden={!isVisible || undefined}
      inert={!isVisible || undefined}
      className={cx(
        "col-start-1 row-start-1 min-w-0 will-change-transform",
        !isVisible && "pointer-events-none",
      )}
      data-test="followed course animated lesson column"
      data-day={day}
    >
      {children}
    </motion.div>
  )
}

function StepButton({
  label,
  direction,
  step,
  isDisabled,
}: {
  label: string
  direction: 1 | -1
  step: (direction: 1 | -1) => void
  isDisabled: boolean
}) {
  return (
    <AriaButton
      aria-label={label}
      data-test={
        direction === -1 ? "followed course previous lessons" : "followed course next lessons"
      }
      onPress={() => step(direction)}
      isDisabled={isDisabled}
      className="group flex shrink-0 cursor-pointer items-center justify-center rounded-[3.75rem] border border-white bg-white text-xs leading-none text-gray-500 shadow-md outline-hidden transition-[border,background-color] duration-200 hover:text-link-blue hover:shadow-[0_0.1875rem_0.75rem_hsla(0,0%,20%,0.7)] focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring active:shadow-[inset_0_0.1875rem_0.75rem_hsla(0,0%,20%,0.3)] disabled:cursor-default disabled:opacity-40 disabled:hover:text-gray-500 disabled:hover:shadow-md"
    >
      <FollowedCourseArrowIcon direction={direction} />
    </AriaButton>
  )
}
